import React, { useState, useEffect, useRef } from 'react';
import { Home as HomeIcon } from 'lucide-react';
import Home from './components/Home';
import Story from './components/Story';
import BoxArt from './components/BoxArt';
import Character from './components/Character';
import Banners from './components/Banners';
import Progress from './components/Progress';
import Team from './components/Team';
import Pull from './components/Pull';
import Missions from './components/Missions';
import Start from './components/Start';
import Characters from './components/Characters';
import RewardModal from './components/RewardModal';
import Game from './components/Game';
import Chat from './components/Chat';
import Friends from './components/Friends';
import Ranking from './components/Ranking';
import Settings from './components/Settings';
import Shop from './components/Shop';
import Daily from './components/Daily';
import BottomNav from './components/BottomNav';
import HUD from './components/HUD';
import { eventBus } from './mockData';
import { recomputePlayerData } from './utils/gameUtils';
import { bottomNavConfig } from './appConfig';
import { PLAYER } from './data';
import Upgrades from './components/Upgrades';

const SCENES_WITH_NAV = bottomNavConfig.screens.map((screen) => screen.id);
const SCENES_WITH_HUD = bottomNavConfig.screens.map((screen) => screen.id);

function App() {
  const [currentScene, setCurrentScene] = useState('start');
  const [chapterId, setChapterId] = useState(null);
  const [characterId, setCharacterId] = useState(null);
  const [battleId, setBattleId] = useState(null);
  const [levelId, setLevelId] = useState(null);
  const [rewardModalData, setRewardModalData] = useState(null);
  const [currentBannerId, setCurrentBannerId] = useState(null);
  const [pullBannerId, setPullBannerId] = useState(null);
  const [chatId, setChatId] = useState(null);
  const [gameSimulation, setGameSimulation] = useState(null);
  const [gameType, setGameType] = useState(null);

  useEffect(() => {
    window.eventBus = eventBus;
    recomputePlayerData();
  }, []);

  const app = {
    setCurrentScene,
    getCurrentScene: () => currentScene,
    goToMenu() {
      setCurrentScene('home');
    },
    goToHome() {
      setCurrentScene('home');
    },
    goToStory(chapterId, callback) {
      setChapterId(chapterId);
      window.leaveSceneCallback = callback;
      setCurrentScene('story');
    },
    goToGame(gameType, levelId, callback) {
      setGameType(gameType);
      setLevelId(levelId);
      window.leaveSceneCallback = callback;
      setCurrentScene('game');
    },
    goToBattle(battleType, battleId) {
      setBattleId(battleId);
      setCurrentScene(battleType);
    },
    goToBoxArt() {
      setCurrentScene('boxart');
    },
    goToCharacter(characterId = null) {
      setCharacterId(characterId);
      setCurrentScene('character');
    },
    goToBanners(bannerId) {
      setCurrentBannerId(bannerId);
      setCurrentScene('banners');
    },
    goToProgress() {
      setCurrentScene('progress');
    },
    goToFriends() {
      setCurrentScene('friends');
    },
    goToRanking() {
      setCurrentScene('ranking');
    },
    goToPull(characterIds, bannerId) {
      setCharacterId(characterIds);
      setPullBannerId(bannerId);
      setCurrentScene('pull');
    },
    goToMissions() {
      setCurrentScene('missions');
    },
    goToShop() {
      setCurrentScene('shop');
    },
    goToDaily() {
      setCurrentScene('daily');
    },
    goToTeam() {
      setCurrentScene('team');
    },
    goToCharacters() {
      setCurrentScene('characters');
    },
    goToStart() {
      setCurrentScene('start');
    },
    goToSettings() {
      setCurrentScene('settings');
    },
    goToChat(chatId) {
      if (!chatId && PLAYER.chats.unreadChatIds.length > 0) {
        chatId = PLAYER.chats.unreadChatIds[0];
      }
      setChatId(chatId);
      setCurrentScene('chat');
    },
    goToUpgrades() {
      setCurrentScene('upgrades');
    },
    goToEndOfContent() {
      console.warn('end of content reached. A page should be shown here');
    },
    showReward(rewardData, callback) {
      setRewardModalData({ rewards: rewardData, callback: callback });
    },
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === 'Escape') {
        app.goToHome();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const renderScene = () => {
    const sceneProps = { app };
    return (
      <div className="fixed inset-0 w-full h-full">
        {SCENES_WITH_HUD.includes(currentScene) && (
          <div className="absolute top-0 left-0 w-full z-50">
            <HUD />
          </div>
        )}
        <div
          className={`h-full ${
            SCENES_WITH_NAV.includes(currentScene) ? 'pb-20' : ''
          }`}
        >
          {currentScene === 'start' ? (
            <Start {...sceneProps} />
          ) : currentScene === 'home' ? (
            <Home {...sceneProps} />
          ) : currentScene === 'boxart' ? (
            <BoxArt {...sceneProps} />
          ) : currentScene === 'character' ? (
            <Character {...sceneProps} characterId={characterId} />
          ) : currentScene === 'banners' ? (
            <Banners {...sceneProps} bannerId={currentBannerId} />
          ) : currentScene === 'progress' ? (
            <Progress {...sceneProps} />
          ) : currentScene === 'friends' ? (
            <Friends {...sceneProps} />
          ) : currentScene === 'ranking' ? (
            <Ranking {...sceneProps} />
          ) : currentScene === 'settings' ? (
            <Settings {...sceneProps} />
          ) : currentScene === 'pull' ? (
            <Pull
              {...sceneProps}
              characterIds={characterId}
              bannerId={pullBannerId}
            />
          ) : currentScene === 'missions' ? (
            <Missions {...sceneProps} />
          ) : currentScene === 'characters' ? (
            <Characters {...sceneProps} />
          ) : currentScene === 'team' ? (
            <Team {...sceneProps} />
          ) : currentScene === 'story' ? (
            <Story {...sceneProps} chapterId={chapterId} onLeaveStory={window.leaveSceneCallback} />
          ) : currentScene === 'game' ? (
            <Game {...sceneProps} gameType={gameType} levelId={levelId} onLeaveGame={window.leaveSceneCallback} />
          ) : currentScene === 'chat' ? (
            <Chat {...sceneProps} chatId={chatId} />
          ) : currentScene === 'shop' ? (
            <Shop {...sceneProps} />
          ) : currentScene === 'daily' ? (
            <Daily {...sceneProps} />
          ) : currentScene === 'upgrades' ? (
            <Upgrades {...sceneProps} />
          ) : (
            <div>Invalid scene: {currentScene}</div>
          )}
        </div>
        {SCENES_WITH_NAV.includes(currentScene) && <BottomNav app={app} />}
        {!SCENES_WITH_NAV.includes(currentScene) &&
          currentScene !== 'start' &&
          currentScene !== 'home' &&
          currentScene !== 'game' && (
            <button
              onClick={() => app.goToHome()}
              className="fixed bottom-4 left-4 z-50 p-3 bg-gray-800 rounded-full text-white/50 hover:text-white/80 transition-colors"
            >
              <HomeIcon size={24} />
            </button>
          )}
      </div>
    );
  };

  return (
    <>
      {renderScene()}
      {rewardModalData && (
        <RewardModal
          app={app}
          rewards={rewardModalData.rewards}
          onClose={() => {
            const callback = rewardModalData.callback;
            setRewardModalData(null);
            if (callback) {
              callback();
            }
          }}
        />
      )}
    </>
  );
}

export default App;
